<template>
  <main :aria-label="$t('general.tag_list')" class="hashtag-list-page">
    <CompanyBreadcrumb :aria-label="$t('general.tag_list')"
                       :home-aria-label="$t('general.home')"
                       :items="breadcrumbItems"
    />

    <template v-if="errorMessages && errorMessages.length">
      <div v-for="(error, index) in errorMessages"
           :key="index"
           class="card hashtag-alert-card">
        <div class="uk-alert-danger" uk-alert>
          <a class="uk-alert-close" uk-close></a>
          <p>
            <i class="uil-exclamation-triangle"></i> {{ error }}
          </p>
        </div>
      </div>
    </template>

    <div v-if="successMessage" class="card hashtag-alert-card">
      <div class="uk-alert-success" uk-alert>
        <a class="uk-alert-close" uk-close></a>
        <p>
          <i class="uil-check"></i> {{ successMessage }}
        </p>
      </div>
    </div>

    <div class="section-header mb-lg-2 border-0 uk-flex-middle hashtag-page-header">
      <div class="section-header-left">
        <h2 class="hashtag-page-title">
          <span>{{ $t("hashtag.management") }}</span>
        </h2>
      </div>
      <div class="section-header-right">
        <button :disabled="!$hasPermission('create_hashtag')"
                :style="{ cursor: !$hasPermission('create_hashtag') ? 'not-allowed' : 'pointer' }"
                class="btn btn-default hashtag-add-btn"
                type="button"
                @click="$router.push({ name: 'company.hashtags.add' })">
          <i class="uil-plus"></i> {{ $t("hashtag.add") }}
        </button>
      </div>
    </div>

    <div class="card hashtag-list-card">
      <!-- Card header -->
      <div class="card-header actions-toolbar border-0 hashtag-toolbar">
        <div class="d-flex justify-content-between hashtag-toolbar-inner">
          <CompanySearchInput v-model="filter.search"
                              :placeholder="$t('general.search')"
                              class="hashtag-toolbar-search"
                              @input="debouncedFilterSearch"
          />
          <div class="d-flex hashtag-toolbar-actions">
            <sort-dropdown :sort-options="sortOptions" @sort-selected="onSortSelected"/>
          </div>
        </div>
      </div>
      <!-- Table -->
      <div class="sf-table sf-table--zebra sf-table--hover hashtag-table-wrap">
        <table>
          <thead>
            <tr>
              <th scope="col">{{ $t("hashtag.name") }}</th>
              <th scope="col">{{ $t("general.added_at") }}</th>
              <th class="sf-table__col-actions" scope="col"></th>
            </tr>
          </thead>
          <tbody class="list">
            <tr v-for="hashtag in formattedItem" :key="hashtag.id">
              <th scope="row">
                <div class="sf-table__media">
                  <router-link :to="{
                    name: 'company.hashtags.edit',
                    params: { id: hashtag.id },
                  }" class="sf-table__name">
                    {{ hashtag.name }}
                  </router-link>
                </div>
              </th>
              <td>{{ hashtag.created_at }}</td>
              <td class="sf-table__col-actions">
                <div class="sf-table__actions">
                  <router-link :to="{
                    name: 'company.hashtags.edit',
                    params: { id: hashtag.id },
                  }" class="btn btn-icon btn-hover btn-sm btn-circle" :uk-tooltip="$t('faq_categories.edit')">
                    <i class="uil-edit"></i>
                  </router-link>
                  <a href="javascript:void(0)"
                     class="btn btn-icon btn-hover btn-sm btn-circle"
                     :uk-tooltip="$t('general.delete')"
                     @click="removeHashtag(hashtag.id)">
                    <i class="uil-minus-circle text-danger"></i>
                  </a>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <pagination v-if="pagination && pagination.hasOwnProperty('links')"
                  :pagination="pagination"
                  class="mt-2 mb-3 hashtag-pagination"
                  @changePage="setPage($event)"/>
    </div>
  </main>
</template>

<script>
import store from "@/core/services";
import Pagination from "@/assets/components/Pagination";
import hashtag_module, {
  BASE_URL as HASHTAG_BASE_URL,
  MODULE_NAME as HASHTAG_MODULE_NAME,
  ERROR as HASHTAG_ERROR,
  ITEMS as HASHTAG_ITEMS,
  GET_ITEMS as HASHTAG_GET_ITEMS,
  DELETE_ITEM as HASHTAG_DELETE_ITEM,
} from "@/core/services/store/hashtag.module";
import moment from "moment";
import debounce from "lodash/debounce";
import { confirmDialogDelete } from "@/utils/confirmDialog";
import CompanyBreadcrumb from "@/view/company/components/breadcrumb/CompanyBreadcrumb.vue";
import CompanySearchInput from "@/view/company/components/search/CompanySearchInput.vue";
import SortDropdown from "@/view/components/dropdown/SortDropdown.vue";
export default {
  name: "HastagList",
  components: { Pagination, CompanyBreadcrumb, CompanySearchInput, SortDropdown },
  data() {
    return {
      status_id: null,
      pagination: null,
      activeItem: null,
      successMessage: null,
      errorMessages: [],
      page: 1,
      per_page: 10,
      sort_by: "id",
      sort_desc: "desc",
      filter: {
        search: null,
        orderBy: "id",
        sort: "desc",
      },

    };
  },
  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    }
    registerStoreModule(HASHTAG_MODULE_NAME, hashtag_module);
  },
  computed: {
    breadcrumbItems() {
      return [
        { name: this.$t("general.tags") },
        { name: this.$t("general.tag_list") },
      ];
    },
    sortOptions() {
      return [
        { key: "name-asc", sort: "name", order: "asc", label: this.$t("sorting.a_z") },
        { key: "name-desc", sort: "name", order: "desc", label: this.$t("sorting.z_a") },
        { key: "created_at-asc", sort: "created_at", order: "asc", label: this.$t("sorting.created_at_asc") },
        { key: "created_at-desc", sort: "created_at", order: "desc", label: this.$t("sorting.created_at_desc") },
      ];
    },
    items: {
      get() {
        return store.getters[HASHTAG_MODULE_NAME + "/" + HASHTAG_ITEMS];
      },
      set(value) { },
    },
    error: {
      get() {
        return store.getters[HASHTAG_MODULE_NAME + "/" + HASHTAG_ERROR];
      },
      set(value) { },
    },
    formattedItem() {
      let results = [];
      if (this.items === null || !("data" in this.items)) return [];
      this.pagination = this.items.data;

      this.items.data.data.forEach((currentItem) => {
        results.push({
          id: currentItem.id,
          name: currentItem.name,
          status: currentItem.status_id
            ? this.$t("general.active")
            : this.$t("general.passive"),
          created_at: currentItem.created_at
            ? moment(currentItem.created_at).format("DD/MM/YYYY HH:mm:ss")
            : "-",
        });
      });

      return results;
    },
  },
  methods: {
    getHashtagResult() {
      let self = this;
      this.$store.dispatch(HASHTAG_MODULE_NAME + "/" + HASHTAG_GET_ITEMS, {
        url: HASHTAG_BASE_URL,
        filters: {
          datatable: true,
          like: self.filter.search,
          page: self.page,
          per_page: self.per_page,
          sort: self.filter.sort,
          order_by: self.filter.orderBy,
          status_id: self.status_id,
        },
      });
    },
    async removeHashtag(id) {
      const isConfirmed = await confirmDialogDelete({ context: this });

      if (isConfirmed) {
        this.$store
          .dispatch(HASHTAG_MODULE_NAME + "/" + HASHTAG_DELETE_ITEM, {
            url: HASHTAG_BASE_URL + "/" + id,
          })
          .then(() => {
            if (!this.error) {
              this.successMessage = this.$t("general.successfully_deleted");
              this.getHashtagResult();
            } else this.errorMessages.push(this.error);
          });
      }

    },
    onSortSelected({ sort, order }) {
      this.page = 1;
      this.filter.orderBy = sort;
      this.filter.sort = order;
      this.getHashtagResult();
    },
    sortOrder(orderBy, sort) {
      this.page = 1;
      this.filter.orderBy = orderBy;
      this.filter.sort = sort;
      this.getHashtagResult();
    },
    setPage(page, is_user = true) {
      let self = this;
      self.page = page;
      this.getHashtagResult();
    },
    resetMessages() {
      this.successMessage = null;
      this.errorMessages = [];
    },
  },
  mounted() {
    // if(!this.GiftVisible){
    //   this.$router.push({name:'company.dashboard'})
    // }
    this.getHashtagResult();
  },
  watch: {
    successMessage: function (val) {
      this.debouncedResetValues();
    },
    errorMessages: function (val) {
      this.debouncedResetValues();
    },
  },
  created: function () {
    this.debouncedFilterSearch = debounce(this.getHashtagResult, 500);
    this.debouncedResetValues = _.debounce(this.resetMessages, 2000);
  },
};
</script>
<style scoped>
.hashtag-list-page {
  --hashtag-primary: var(--primaryColor, #3e416d);
  --hashtag-text: #1e293b;
  --hashtag-muted: #64748b;
  --hashtag-border: #e2e8f0;
  --hashtag-soft: #f6f7fb;
  min-width: 0;
}

.hashtag-alert-card {
  margin-bottom: 16px;
  overflow: hidden;
  border: 1px solid var(--hashtag-border);
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.hashtag-list-page .uk-alert {
  margin: 0;
}

.hashtag-list-page .uk-alert p {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
}

.hashtag-list-page .uk-alert-danger p {
  color: #dc3545;
}

.hashtag-list-page .uk-alert-success p {
  color: #28a745;
}

.hashtag-page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.hashtag-page-header .section-header-right > * {
  margin-left: 0;
}

.hashtag-page-title {
  margin: 0;
  color: var(--hashtag-text);
  font-size: 19px;
  font-weight: 600;
  line-height: 1.3;
}

.hashtag-add-btn {
  padding: 10px 17px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  line-height: 17px;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.hashtag-add-btn:active:not(:disabled) {
  transform: scale(0.98);
  opacity: 0.85;
}

.hashtag-add-btn:disabled {
  opacity: 0.5;
  pointer-events: none;
}

.hashtag-list-card {
  overflow: hidden;
  margin-top: 0;
  border: 1px solid var(--hashtag-border);
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.hashtag-toolbar {
  padding: 16px;
}

.hashtag-toolbar-inner {
  align-items: center;
  gap: 16px;
}

.hashtag-toolbar-search {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 480px;
}

.hashtag-toolbar-inner ::v-deep .company-search-input,
.hashtag-toolbar-inner ::v-deep .section-header-left {
  float: none;
  width: 100%;
  max-width: none;
  margin: 0;
}

.hashtag-toolbar-inner ::v-deep .company-search-input input {
  height: auto;
  min-height: 0;
  padding: 10px 16px;
  font-size: 14px;
}

.hashtag-toolbar-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
}

.hashtag-toolbar-actions ::v-deep .btn-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.hashtag-toolbar-actions ::v-deep .btn-circle:active {
  transform: scale(0.98);
  opacity: 0.85;
}

/* Tablo tasarimi .sf-table'dan gelir (assets/scss/_data-table.scss).
   Burada yalnizca sayfaya ozgu degerler ayarlanir. */

.hashtag-pagination {
  padding: 0 8px 8px;
}

input[type="button"],
input[type="submit"],
span.btn,
.btn {
  display: inline-block;
}

@media (max-width: 959px) {
  .hashtag-page-header {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .hashtag-page-header .section-header-left,
  .hashtag-page-header .section-header-right {
    width: 100%;
  }

  .hashtag-page-title {
    font-size: 24px;
    font-weight: 700;
    line-height: 1.3;
  }

  .hashtag-list-page .uk-alert p {
    font-size: 16px;
  }

  .hashtag-add-btn {
    width: 100%;
    min-height: 48px;
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.4;
  }

  .hashtag-toolbar-inner ::v-deep .company-search-input input {
    height: 48px;
    min-height: 48px;
    padding: 12px 16px;
    font-size: 16px;
  }

  .hashtag-toolbar-actions ::v-deep .btn-circle {
    width: 48px;
    height: 48px;
    min-width: 48px;
    min-height: 48px;
  }

  .hashtag-toolbar-inner {
    flex-direction: column;
    align-items: stretch;
  }

  .hashtag-toolbar-search {
    max-width: none;
    width: 100%;
  }

  /* toolbar-inner mobilde column'a dönüyor; aksiyonlar tam genişlik
     kaplayıp sola yapışmasın diye sağa yaslanıyor (UserPassive ile aynı). */
  .hashtag-toolbar-actions {
    flex-wrap: wrap;
    align-self: flex-end;
    width: auto;
    max-width: 100%;
    margin-left: auto;
    justify-content: flex-end;
  }

}

@media (max-width: 767px) {
  .hashtag-list-page {
    margin: 0;
    padding: 0 0 24px;
  }

  .hashtag-page-title {
    font-size: 24px;
  }

  .hashtag-list-card {
    border-radius: 14px;
  }

  .hashtag-toolbar {
    padding: 16px;
  }

  .hashtag-toolbar-actions {
    gap: 8px;
  }

  /* Tablo mobilde sikistirilmaz, yatay kaydirilir */
  .hashtag-table-wrap {
    --sf-table-min-width: 500px;
  }

  .hashtag-pagination {
    padding: 0 16px 16px;
  }
}

@media (hover: hover) and (pointer: fine) {
  .hashtag-add-btn:hover:not(:disabled) {
    opacity: 0.92;
    cursor: pointer;
  }
}

@media (hover: none) {
  .hashtag-list-page ::v-deep .btn-hover:hover {
    background-color: transparent;
  }
}

@media (prefers-reduced-motion: reduce) {
  .hashtag-add-btn:active:not(:disabled),
  .hashtag-toolbar-actions ::v-deep .btn-circle:active {
    transform: none;
    opacity: 1;
  }
}
</style>
